/**
 * 用于计算成本分摊
 * Create by 仇兴洲
 */

import {
  add,
  divideDown,
  multiplyRound,
  subtractRound,
} from '@/libs/util.number'

/**
 * 获取成本分摊
 * @param {*} model 分摊类型
 * @param {*} detailList 需要分摊的费用列表
 * @param {*} cost 费用信息
 * @returns 分摊后的费用列表
 */
export function getCostShare(model, detailList, cost) {
  if (model === '1') return splitByOrderCount(detailList, cost)
  // 件数
  if (model === '2') return splitByQty(detailList, cost)
  // 重量
  if (model === '3') return splitByWeight(detailList, cost)
  // 体积
  if (model === '4') return splitByVolume(detailList, cost)
  // 货值
  if (model === '9') return splitByValue(detailList, cost)

  return detailList.map((item) => ({
    ...item,
    payFreight: 0,
    payFee: 0,
    payAmount: 0,
    contractFreight: 0,
    contractFee: 0,
    contractAmount: 0,
  }))
}

/**
 * 按订单数分摊
 * @param {*} detailList 需要分摊的费用列表
 * @param {*} cost 费用信息
 * @returns 分摊后的费用列表
 */
export function splitByOrderCount(detailList, cost) {
  return split(detailList, cost, detailList.length, (n) => n)
}

/**
 * 按件数分摊
 * @param {*} detailList 需要分摊的费用列表
 * @param {*} cost 费用信息
 * @returns 分摊后的费用列表
 */
export function splitByQty(detailList, cost) {
  const total = detailList
    .map((item) => item.totQty)
    .reduce((total, next) => add(total, next))
  return split(detailList, cost, total, (n, item) =>
    multiplyRound(n, item.totQty, 4)
  )
}

/**
 * 按重量分摊
 * @param {*} detailList 需要分摊的费用列表
 * @param {*} cost 费用信息
 * @returns 分摊后的费用列表
 */
export function splitByWeight(detailList, cost) {
  const total = detailList
    .map((item) => item.totPayWeight)
    .reduce((total, next) => add(total, next))

  return split(detailList, cost, total, (n, item) =>
    multiplyRound(n, item.totPayWeight, 4)
  )
}

/**
 * 按体积分摊
 * @param {*} detailList 需要分摊的费用列表
 * @param {*} cost 费用信息
 * @returns 分摊后的费用列表
 */
export function splitByVolume(detailList, cost) {
  const total = detailList
    .map((item) => item.totPayVolume)
    .reduce((total, next) => add(total, next))

  return split(detailList, cost, total, (n, item) =>
    multiplyRound(n, item.totPayVolume, 4)
  )
}

/**
 * 按货值分摊
 * @param {*} detailList 需要分摊的费用列表
 * @param {*} cost 费用信息
 * @returns 分摊后的费用列表
 */
export function splitByValue(detailList, cost) {
  const total = detailList
    .map((item) => item.totValue)
    .reduce((total, next) => add(total, next))

  return split(detailList, cost, total, (n, item) =>
    multiplyRound(n, item.totValue, 4)
  )
}

/**
 * 分摊
 * @param {*} detailList 需要分摊的费用列表
 * @param {*} cost 费用信息
 * @param {*} total 求单价的除数
 * @param {*} cacl 计算分摊费用的计算公式 参数(单价,当前项)
 * @returns 分摊后的费用列表
 */
export function split(detailList, cost, total, cacl) {
  if (!detailList || !detailList.length) return

  cost = {
    payFreight: cost.payFreight || 0,
    payFee: cost.payFee || 0,
    payAmount: cost.payAmount || 0,
    contractFreight: cost.contractFreight || 0,
    contractFee: cost.contractFee || 0,
    contractAmount: cost.contractAmount || 0,
  }

  // 计算每份的费用
  let payFreight = divideDown(cost.payFreight, total, 10)
  let payFee = divideDown(cost.payFee, total, 10)
  let payAmount = divideDown(cost.payAmount, total, 10)
  let contractFreight = divideDown(cost.contractFreight, total, 10)
  let contractFee = divideDown(cost.contractFee, total, 10)
  let contractAmount = divideDown(cost.contractAmount, total, 10)

  let totalPayFreight = 0
  let totalPayFee = 0
  let totalPayAmount = 0
  let totalContractFreight = 0
  let totalContractFee = 0
  let totalContractAmount = 0

  for (let i = 0; i < detailList.length; i++) {
    const item = detailList[i]

    let itemPayFreight = cacl(payFreight, item)
    let itemPayFee = cacl(payFee, item, item)
    let itemPayAmount = cacl(payAmount, item)
    let itemContractFreight = cacl(contractFreight, item)
    let itemContractFee = cacl(contractFee, item)
    let itemContractAmount = cacl(contractAmount, item)

    if (i + 1 === detailList.length) {
      item.payFreight = subtractRound(cost.payFreight, totalPayFreight, 4)
      item.payFee = subtractRound(cost.payFee, totalPayFee, 4)
      item.payAmount = subtractRound(cost.payAmount, totalPayAmount, 4)
      item.contractFreight = subtractRound(
        cost.contractFreight,
        totalContractFreight,
        4
      )
      item.contractFee = subtractRound(cost.contractFee, totalContractFee, 4)
      item.contractAmount = subtractRound(
        cost.contractAmount,
        totalContractAmount,
        4
      )
    } else {
      totalPayFreight = add(totalPayFreight, itemPayFreight)
      totalPayFee = add(totalPayFee, itemPayFee)
      totalPayAmount = add(totalPayAmount, itemPayAmount)
      totalContractFreight = add(totalContractFreight, itemContractFreight)
      totalContractFee = add(totalContractFee, itemContractFee)
      totalContractAmount = add(totalContractAmount, itemContractAmount)
      item.payFreight = itemPayFreight
      item.payFee = itemPayFee
      item.payAmount = itemPayAmount
      item.contractFreight = itemContractFreight
      item.contractFee = itemContractFee
      item.contractAmount = itemContractAmount
    }
  }
  return detailList
}

export default {
  methods: {
    getCostShare,
    splitByOrderCount,
    splitByQty,
    splitByWeight,
    splitByVolume,
    splitByValue,
    checkCostShare(orgin, totalInfo) {
      if (orgin.payFreight && totalInfo.payFreight) {
        if (orgin.payFreight !== totalInfo.payFreight) {
          this.$message.error('分摊后的支付运费合计与实际支付运费不符,请检查!')
          return false
        }
      }
      if (orgin.payFee && totalInfo.payFee) {
        if (orgin.payFee !== totalInfo.payFee) {
          this.$message.error(
            '分摊后的支付附加费用合计与实际支付附加费用不符,请检查!'
          )
          return false
        }
      }
      if (orgin.payAmount && totalInfo.payAmount) {
        if (orgin.payAmount !== totalInfo.payAmount) {
          this.$message.error(
            '分摊后的支付总金额合计与实际支付总金额不符,请检查!'
          )
          return false
        }
      }
      if (orgin.contractFreight && totalInfo.contractFreight) {
        if (orgin.contractFreight !== totalInfo.contractFreight) {
          this.$message.error('分摊后的合同运费合计与实际合同运费不符,请检查!')
          return false
        }
      }
      if (orgin.contractFee && totalInfo.contractFee) {
        if (orgin.contractFee !== totalInfo.contractFee) {
          this.$message.error(
            '分摊后的合同附加费用合计与实际合同附加费用不符,请检查!'
          )
          return false
        }
      }
      if (orgin.contractAmount && totalInfo.contractAmount) {
        if (orgin.contractAmount !== totalInfo.contractAmount) {
          this.$message.error(
            '分摊后的合同总金额合计与实际合同总金额不符,请检查!'
          )
          return false
        }
      }

      return true
    },
  },
}
